import { reducer as formReducer } from 'redux-form';
import { combineReducers } from 'redux';
import { handleActions } from 'redux-actions';
import moment from 'moment';

import { COUNTRY_CZ } from '../enums/countries';
import StayType from '../enums/StayType';

import {
	SET_INITIATED,
	SET_STEP,
	SET_MAX_STEP,
	SET_LOADING,
	SET_ROOM_OFFERS,
	SET_API_ROOM_TYPES_AVAILABILITIES_RESPONSE,
	SET_API_ROOMS_RESPONSE,
	SET_API_DATA_RESPONSE,
	SET_STORNO_INSURANCE_PRICE,
	SET_TOTAL_PRICE,
	SET_PRICE_TO_PAY,
	SET_SELECTED_ROOM_TYPE,
	SET_RESERVATION_STATUS,
	SET_TOTAL_NO_OF_GUESTS,
	SET_NO_OF_NIGHTS,
	SET_LANG,
	SET_TRANSLATIONS,
} from './actions';

import { DATE_FORMAT } from '../constants';

const appActionHandlers = {
	[SET_INITIATED]: (state, action) => ({
		...state,
		initiated: action.payload,
	}),
	[SET_STEP]: (state, action) => {
		const newState = {
			...state,
			step: action.payload,
		};

		if (newState.step > newState.maxStep) {
			newState.maxStep = newState.step;
		}

		return newState;
	},
	[SET_MAX_STEP]: (state, action) => ({
		...state,
		setMaxStep: action.payload,
	}),
	[SET_LANG]: (state, action) => ({
		...state,
		lang: action.payload,
	}),
	[SET_LOADING]: (state, action) => ({
		...state,
		isLoading: action.payload,
	}),
	[SET_ROOM_OFFERS]: (state, action) => ({
		...state,
		roomOffers: action.payload,
	}),
	[SET_API_ROOMS_RESPONSE]: (state, action) => ({
		...state,
		apiRoomsResponse: action.payload,
	}),
	[SET_API_ROOM_TYPES_AVAILABILITIES_RESPONSE]: (state, action) => ({
		...state,
		apiRoomTypesAvailabilitiesResponse: action.payload,
	}),
	[SET_API_DATA_RESPONSE]: (state, action) => {
		const apiDataResponse = action.payload;
		delete apiDataResponse.css;
		return Object.assign(state, apiDataResponse);
	},
	[SET_STORNO_INSURANCE_PRICE]: (state, action) => ({
		...state,
		stornoInsurancePrice: action.payload,
	}),
	[SET_TOTAL_PRICE]: (state, action) => ({
		...state,
		totalPrice: action.payload,
	}),
	[SET_PRICE_TO_PAY]: (state, action) => ({
		...state,
		priceToPay: action.payload,
	}),
	[SET_SELECTED_ROOM_TYPE]: (state, action) => ({
		...state,
		selectedRoomType: action.payload,
	}),
	[SET_RESERVATION_STATUS]: (state, action) => ({
		...state,
		reservationStatus: action.payload,
	}),
	[SET_TOTAL_NO_OF_GUESTS]: (state, action) => ({
		...state,
		totalNoOfGuests: action.payload,
	}),
	[SET_NO_OF_NIGHTS]: (state, action) => ({
		...state,
		noOfNights: action.payload,
	}),
	[SET_TRANSLATIONS]: (state, action) => ({
		...state,
		translations: {
			...state.translations,
			[action.payload.lang]: action.payload.translations,
		}
	})
};

const appDefaultInitialValues = {
	apiOrigin: 'http://res.plazaro.loc',
	// apiOrigin: 'https://res.plazaro-dev.com',
	hotelId: 'clyxut',
	// hotelId: 'ad8vei',
	lang: 'cs',
	theme: 'system',
	// Pokud je 1, tak zobrazovat všechny pokoje (i ty obsazené)
	all: 0,
	// Používá se především jako query parametr ?selectedRoomType=<room_type_id> pro zobrazení konkreního typu pokoje
	selectedRoomType: null, // více hodnot je možné oddělit čárkou
	guests: 1, // výchozí počet hostů na pokoji
	step: 1,
	maxStep: 1,
	apiRoomsResponse: null,
	isLoading: true,
	roomOffers: [],
	stornoInsurancePrice: 0,
	totalPrice: 0,
	priceToPay: 0,
	totalNoOfGuests: 0,
	noOfNights: 1,
	// vyska fixniho menu na strance (pri vlozeni do iframu, kvuli scrollovani nahoru)
	fixedHeaderHeight: 0,
	reservationStatus: null,
	initiated: false,
};

export const bookingFormInitialValues = {
	arrival: moment().format(DATE_FORMAT),
	departure: moment().add(1, 'day').format(DATE_FORMAT),
	rooms: {},

	// Typ pobytu
	stayType: StayType.VACATION,

	// Strava
	boardBase: null,

	isCompany: false,

	firstName: process.env.NODE_ENV === 'development' ? 'Adam' : '',
	lastName: process.env.NODE_ENV === 'development' ? 'Štěpánek' : '',
	phone: process.env.NODE_ENV === 'development' ? '+420775112448' : '+420',
	email: process.env.NODE_ENV === 'development' ? 'adam@appsdevteam.com' : '',
	street: process.env.NODE_ENV === 'development' ? 'Křížkovského 33a' : '',
	city: process.env.NODE_ENV === 'development' ? 'Brno' : '',
	postCode: process.env.NODE_ENV === 'development' ? '60300' : '',

	// Česká republika
	companyCountry: COUNTRY_CZ,

	paymentMethod: null,
	advancePaymentOnly: false,

	stornoInsurance: false,

	lang: 'cs',
};

const query = new URLSearchParams(window.location.search);

const arrival = query.get('arrival') ? moment(query.get('arrival'), 'YYYY-MM-DD') : null;
const departure = query.get('departure') ? moment(query.get('departure'), 'YYYY-MM-DD') : null;

if (arrival) {
	bookingFormInitialValues.arrival = arrival.format(DATE_FORMAT);
	bookingFormInitialValues.departure = departure
		? departure.format(DATE_FORMAT)
		: arrival.add(1, 'day').format(DATE_FORMAT);
} else if (departure) {
	bookingFormInitialValues.departure = departure.format(DATE_FORMAT);
}

export default function(initialValues) {

	if (initialValues.step) {
		initialValues.step = parseInt(initialValues.step, 10);
	}

	if (initialValues.guests) {
		initialValues.guests = parseInt(initialValues.guests, 10);
		if (isNaN(initialValues.guests) || initialValues.guests < 1) {
			initialValues.guests = 1;
		}
	}

	if (initialValues.selectedRoomType !== undefined) {
		initialValues.selectedRoomType = initialValues.selectedRoomType.split(',').map(val => parseInt(val, 10)).filter(val => !isNaN(val));
		if (initialValues.selectedRoomType.length === 0) {
			initialValues.selectedRoomType = null;
		}
	}

	const appInitialValues = Object.assign(appDefaultInitialValues, initialValues);

	return combineReducers({
		app: handleActions(appActionHandlers, appInitialValues),
		form: formReducer
	});
}



// WEBPACK FOOTER //
// ./src/store/reducers.js